import {
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControlName,
    NG_VALUE_ACCESSOR,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { TimeSlotInterface } from '@app/core/interfaces';
import { IndicationInterface } from '@app/core/interfaces/indication.interface';
import { LocationInterface } from '@app/core/interfaces/location.interface';
import { ClaimAppointmentInterface } from '@app/my-zone/pages/claims/store/entity/appointment.interface';
import { DateService, DateStruct, Notification } from 'outshared-lib';
import { Subscription } from 'rxjs';
import { AppointmentService } from '../../appointment.service';

@Component({
    selector: 'ins-timeslot-form',
    templateUrl: './timeslot-form.component.html',
    styleUrls: ['timeslot-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: TimeslotFormComponent,
            multi: true,
        },
    ],
})
export class TimeslotFormComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {

    @Input() disabled = false;
    @Input() formControlName: FormControlName;

    @Input() file_serial_number: number;
    @Input() address_relation_id: number;
    @Input() appointment: ClaimAppointmentInterface;
    @Input() location: LocationInterface;
    public datum: string;
    public form: UntypedFormGroup;
    public notifications: Notification[] = [];
    public timeSlots: TimeSlotInterface[] = [];
    public loading: boolean = false;
    public indication: IndicationInterface = null;
    private datumValueChanges: Subscription;
    private timeFrameValueChanges: Subscription;
    private subService: Subscription;

    constructor(
        private service: AppointmentService,
        private dateService: DateService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    @HostBinding('style.opacity') get opacity(): number {
        return this.disabled ? 0.25 : 1;
    }

    get value(): IndicationInterface {
        return this.indication;
    }

    ngOnInit(): void {
        this.datum = this.dateService.nowAsString();
        this.createForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.locatie && this.form.value.datum) {
            this.changeDate(this.form.value.datum);
        }
    }

    ngOnDestroy(): void {
        this.datumValueChanges.unsubscribe();
        this.timeFrameValueChanges.unsubscribe();

        if (this.subService && !this.subService.closed) {
            this.subService.unsubscribe();
        }

        this.writeValue(null);
    }

    /**
     * Control value accessor implentation
     */
    public onChange = (value: IndicationInterface) => {};

    public onTouched(): void {}

    public writeValue(value: IndicationInterface): void {
        if (value !== undefined) {
            this.indication = value;
            this.onChange(value);
        }
    }

    public registerOnChange(fn: (indicatie: IndicationInterface) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private createForm(): void {
        this.form = new UntypedFormGroup({
            datum: new UntypedFormControl('', [Validators.required]),
            tijdstip: new UntypedFormControl('', [Validators.required]),
        });

        this.datumValueChanges = this.form.controls.datum.valueChanges
            .subscribe((val) => {
                this.changeDate(val);
                this.writeValue(null);
            });

        this.timeFrameValueChanges = this.form.controls.tijdstip.valueChanges
            .subscribe((val) => this.onChangeTijdstip(val));
    }

    private changeDate(value): void {
        const datum = value;

        if (datum) {
            if (this.subService && !this.subService.closed) {
                this.subService.unsubscribe();
            }

            this.subService = this.service.requestAppointmentTimeslots(
                this.appointment,
                this.address_relation_id,
                this.file_serial_number,
                this.appointment.netwerk_id,
                datum)
                .subscribe(
                    (result) => this.onTimeSlotSuccess(result),
                    (error) => this.onTimeSlotError(error),
                );

            this.loading = true;
            this.notifications = [];
            this.timeSlots = [];
            this.form.controls.tijdstip.reset();
        }
    }

    private onChangeTijdstip(value): void {
        const date = this.form.get('datum').value;
        const timeFrame = value;

        if (date && timeFrame) {
            this.writeValue({
                datum: date,
                tijdstip: timeFrame,
            });
        }
    }

    private onTimeSlotSuccess(result): void {
        if (result.time_slots) {
            const timeSlots = result.time_slots;
            timeSlots.forEach((timeSlot) => {
                this.timeSlots.push(this.createTimeSlot(timeSlot));
            });
        } else {
            this.notifications = result;
        }

        this.loading = false;
        this.changeDetectorRef.detectChanges();
    }

    private createTimeSlot(timeSlot: Partial<TimeSlotInterface>): TimeSlotInterface {
        return {
            date: timeSlot.date,
            duration: timeSlot.duration,
            event_time: timeSlot.event_time,
            date_time: this.generateTime(timeSlot.date, timeSlot.event_time),
        };
    }

    private generateTime(datum: DateStruct, timePeriod: string): string {
        const _datum = this.dateService.format(datum, 'dd D-M', 'nl');
        return `${ _datum } ${ timePeriod }u`;
    }

    private onTimeSlotError(error): void {
        this.notifications = error;
        this.loading = false;
    }
}
